// Página de Teatro y Ópera (ruta "/teatro").
// Muestra posts traídos de Ghost CMS con la etiqueta 'teatro'.
// Incluye un hero animado con fondo bordeaux, un grid de tarjetas con reseñas,
// un botón al formulario de suscripción y un disclaimer legal al pie.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaInstagram, FaTiktok, FaArrowLeft } from 'react-icons/fa';
import Footer from '../../../components/footer/Footer';
import InfoCard from '../../../components/shared/InfoCard';
import SkeletonCard from '../../../components/shared/SkeletonCard';
import ProductRow from '../../../components/store/ProductRow';
import { getGhostPosts } from '../../../services/ghost';
import styles from './TeatroLanding.module.css';


const TeatroLanding = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Trae hasta 9 posts con etiqueta 'teatro' al montar el componente
  useEffect(() => {
    const fetchTeatroPosts = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const data = await getGhostPosts({ tagSlug: 'teatro', limit: 9 });
        setPosts(data.posts || []);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTeatroPosts();
  }, []);

  return (
    <div id="teatro-landing-page" className={styles['page-wrapper']}>
      <Helmet>
        <title>Teatro y Ópera — Matias Cagliero</title>
        <meta name="description" content="Novedades del Teatro Colón 2026, reseñas de funciones de ópera y análisis de producciones líricas." />
        <link rel="canonical" href="https://cagliero.ar/teatro" />
        <meta property="og:title" content="Teatro y Ópera — Matias Cagliero" />
        <meta property="og:description" content="Novedades del Teatro Colón 2026, reseñas de funciones de ópera y análisis de producciones líricas." />
        <meta property="og:url" content="https://cagliero.ar/teatro" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Barra superior con breadcrumb y redes sociales */}
      <div className={styles['top-bar']}>
        <nav className={styles.breadcrumb}>
          <Link to="/" className={styles['back-button-breadcrumb']}>
            <FaArrowLeft className={styles['back-icon']} />
            <span>Inicio</span>
          </Link>
          <span className={styles['breadcrumb-separator']}>/</span>
          <span className={styles['breadcrumb-current']}>Teatro</span>
        </nav>
        <div className={styles['top-bar-socials']}>
          <a href="https://www.instagram.com/matias_cagliero" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={styles['social-icon']}>
            <FaInstagram />
          </a>
          <a href="https://www.tiktok.com/@matias_cagliero" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className={styles['social-icon']}>
            <FaTiktok />
          </a>
        </div>
      </div>

      <header className={styles['hero-section-v3']}>
        <div className={styles['hero-content']}>
          <h1 className={styles['hero-title']}>Novedades del Teatro Colón 2026</h1>
          <p className={styles['hero-subtitle']}>
            Página informativa con las últimas novedades y guía de suscripciones.
          </p>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/form" className={styles['load-more-btn']}>
              Ir al Formulario
            </Link>
          </div>
        </div>
      </header>

      <main className={styles['data-analysis-content-area']}>
        {/* Fila de productos del ecommerce (si está habilitada en el admin) */}
        <ProductRow category="teatro" />

        <section className={styles['section-v3']}>
          <h2 className={styles['section-title-v3']}>Notas y Artículos</h2>

          {/* Error de API */}
          {error && (
            <p className={styles['loading-message']}>
              No se pudo cargar el contenido. Verificá tu conexión e intentá recargar la página.
            </p>
          )}

          <div className={styles['posts-grid-container']}>
            {isLoading ? (
              // Muestra 6 skeletons mientras cargan los posts
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : posts.length > 0 ? (
              posts.map(post => <InfoCard key={post.id} item={post} variant="list" />)
            ) : !error ? (
              <p className={styles['loading-message']}>No hay notas disponibles por el momento.</p>
            ) : null}
          </div>
        </section>
      </main>

      {/* Disclaimer: esta página no tiene vínculo oficial con el Teatro Colón */}
      <section className={styles['disclaimer-footer']}>
        <div className={styles['disclaimer-text-container']}>
          <p>
            <strong>Deslinde de responsabilidad (Disclaimer):</strong> Este sitio web es una plataforma
            informativa de carácter independiente y gratuito.{' '}
            <strong>Matias Cagliero no posee vínculo jurídico, comercial, ni oficial con el Teatro Colón.</strong>
          </p>
        </div>
      </section>

      <Footer variant="lifestyle" />
    </div>
  );
};

export default TeatroLanding;
